import { type InferArgs } from "./v.js";
import type { MutationDef, MutationHandler } from "./reactive-mutation-types.js";

// ─── mutation registry (globalThis) ─────────────────────
// globalThis shared singleton — dual-bundle safe (see analysis-dual-module-registry.md).

declare global {
  var __gencow_mutationRegistry: (MutationDef<any, any> & { name: string })[];
}

if (!globalThis.__gencow_mutationRegistry) globalThis.__gencow_mutationRegistry = [];

/**
 * @deprecated Internal legacy registry.
 * Use `procedure` from your app's `gencow/runtime.ts` instead.
 */
export const mutationRegistry = globalThis.__gencow_mutationRegistry;

let mutationCounter = 0;

/**
 * mutation — 데이터 변경 함수를 선언적으로 등록합니다.
 *
 * @deprecated Use `procedure.mutation` from your app's `gencow/runtime.ts` instead.
 *
 * 3가지 호출 방식 지원 (query와 동일한 패턴 우선):
 *
 * @example
 * ```typescript
 * // ✅ 권장: query와 동일한 (name, def) 패턴
 * mutation("tasks.create", {
 *     args: { title: v.string() },
 *     handler: async (ctx, args) => { ... },
 * });
 *
 * // ✅ 객체 스타일 (하위 호환)
 * mutation({
 *     name: "tasks.create",
 *     handler: async (ctx) => { ... },
 * });
 *
 * // ⚠️ Legacy 배열 스타일 (비권장)
 * mutation(["tasks.list"], handler, "tasks.create");
 * ```
 *
 * @note invalidates 필드는 deprecated — 전달해도 무시됩니다.
 *       리얼타임 UI 갱신에는 ctx.realtime.emit() 또는 ctx.realtime.refresh()를 사용하세요.
 */
export function mutation<TSchema = any, TReturn = any>(
  nameOrInvalidatesOrDef:
    | string
    | string[]
    | {
        name?: string;
        args?: TSchema;
        public?: boolean;
        invalidates?: string[];
        handler: MutationHandler<InferArgs<TSchema>, TReturn>;
      },
  handlerOrDef?:
    | MutationHandler<InferArgs<TSchema>, TReturn>
    | {
        invalidates?: string[];
        args?: TSchema;
        public?: boolean;
        handler: MutationHandler<InferArgs<TSchema>, TReturn>;
      },
  name?: string,
): MutationDef<TSchema, TReturn> {
  let argsSchema: TSchema | undefined;
  let actualHandler: MutationHandler<InferArgs<TSchema>, TReturn>;
  let mutName: string;
  let isPublic = false;

  if (typeof nameOrInvalidatesOrDef === "string") {
    // New primary style: mutation("name", { args?, public?, handler })
    mutName = nameOrInvalidatesOrDef;
    const def = handlerOrDef as {
      args?: TSchema;
      public?: boolean;
      handler: MutationHandler<InferArgs<TSchema>, TReturn>;
    };
    actualHandler = def.handler;
    argsSchema = def.args;
    isPublic = def.public === true;
  } else if (Array.isArray(nameOrInvalidatesOrDef)) {
    // Legacy style: mutation([...], handler, "name") — invalidates ignored
    actualHandler = handlerOrDef as MutationHandler<InferArgs<TSchema>, TReturn>;
    mutName = name || `mutation_${++mutationCounter}`;
  } else {
    // Object style: mutation({ name?, args?, public?, handler })
    actualHandler = nameOrInvalidatesOrDef.handler;
    argsSchema = nameOrInvalidatesOrDef.args;
    isPublic = nameOrInvalidatesOrDef.public === true;
    mutName =
      nameOrInvalidatesOrDef.name ||
      (typeof name === "string" ? name : "") ||
      `mutation_${++mutationCounter}`;
  }

  // 이름 미지정 시 경고 — 디버깅 지원
  if (mutName.startsWith("mutation_")) {
    console.warn(
      `[gencow] mutation registered without explicit name → "${mutName}". ` +
        `Use mutation("myMutation", { handler }) for better debugging.`,
    );
  }

  const def: MutationDef<TSchema, TReturn> & { name: string } = {
    name: mutName,
    handler: actualHandler,
    argsSchema,
    isPublic,
  };
  mutationRegistry.push(def);
  return def;
}

/**
 * @deprecated Legacy mutation registry API.
 * Use `procedure` from your app's `gencow/runtime.ts` instead.
 */
export function getRegisteredMutations(): (MutationDef & { name: string })[] {
  return [...mutationRegistry];
}
